import { Injectable } from '@angular/core';
import html2canvas from 'html2canvas';
import * as pdfMake from 'pdfmake/build/pdfmake';
import { TDocumentDefinitions } from 'pdfmake/interfaces';

@Injectable({
  providedIn: 'root',
})
export class ChartExportService {
  constructor() {}

  /**
   * Capture a chart element and convert it to a base64 image.
   * @param chartId The ID of the chart element to capture.
   * @returns A promise that resolves to the base64 string of the captured image.
   */
  async captureChart(chartId: string): Promise<string | null> {
    const chartElement = document.getElementById(chartId);
    if (!chartElement) {
      console.error(`Chart with ID '${chartId}' not found.`);
      return null;
    }

    try {
      // Wait for rendering completion
      await new Promise(resolve => setTimeout(resolve, 500));
      const canvas = await html2canvas(chartElement, { scale: 2, useCORS: true });
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error(`Error capturing chart (${chartId}):`, error);
      return null;
    }
  }

  /**
   * Export an array of chart images to a PDF.
   * @param chartIds Array of chart container IDs to capture.
   * @param fileName The name of the output PDF file.
   */
  async exportChartsToPdf(chartIds: string[], fileName: string = 'Dashboard_Report.pdf'): Promise<void> {
    try {
      const content: any[] = []; // Store PDF content

      for (const chartId of chartIds) {
        const chartImage = await this.captureChart(chartId); // Capture chart image
        if (chartImage) {
          content.push({ image: chartImage, width: 500 }); // Add image to PDF
          content.push({ text: '\n' }); // Add spacing
        } else {
          console.error(`Failed to capture chart with ID '${chartId}'.`);
        }
      }

      if (content.length === 0) {
        console.error('No charts were captured for export.');
        return;
      }

      const docDefinition: TDocumentDefinitions = {
        content,
        styles: {
          header: { fontSize: 18, bold: true, margin: [0, 20, 0, 10] },
        },
      };

      pdfMake.createPdf(docDefinition).download(fileName); // Download PDF
    } catch (error) {
      console.error('Error exporting charts:', error);
    }
  }
}
